// Provider Registration Page
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  Building2,
  User,
  Wrench,
  Zap,
  Droplets,
  Flame,
  Home as HomeIcon,
  Paintbrush,
  Grid3X3,
  Hammer,
  Thermometer,
  Sun,
  DoorOpen,
  Square,
  CloudRain,
  Truck,
  Trash2,
  ChefHat,
  MapPin,
  Clock,
  Upload
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";

const apiUrl = (path) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return `${backendUrl}/api${path}`;
};

const SERVICE_CATEGORIES = [
  { id: "electrician", name: "Elektriker", icon: Zap },
  { id: "plumber", name: "Sanitär / SHK", icon: Droplets },
  { id: "heating", name: "Heizungsbauer", icon: Flame },
  { id: "roofer", name: "Dachdecker", icon: HomeIcon },
  { id: "painter", name: "Maler & Lackierer", icon: Paintbrush },
  { id: "tiler", name: "Fliesenleger", icon: Grid3X3 },
  { id: "drywall", name: "Trockenbau", icon: Square },
  { id: "carpenter", name: "Tischler / Schreiner", icon: Hammer },
  { id: "renovation", name: "Renovierung / Sanierung", icon: Wrench },
  { id: "mold", name: "Schimmel / Feuchtigkeit", icon: CloudRain },
  { id: "construction", name: "Bauunternehmen / Rohbau", icon: HomeIcon },
  { id: "solar", name: "Photovoltaik / Solaranlagen", icon: Sun },
  { id: "windows", name: "Fenster & Türen Montage", icon: DoorOpen },
  { id: "insulation", name: "Wärmedämmung / Isolierung", icon: Thermometer },
  { id: "flooring", name: "Bodenleger", icon: Square },
  { id: "facade", name: "Fassadenbau / Fassadenreinigung", icon: HomeIcon },
  { id: "gutters", name: "Regenrinnen & Entwässerung", icon: CloudRain },
  { id: "moving", name: "Möbelmontage / Umzug", icon: Truck },
  { id: "clearance", name: "Entrümpelung", icon: Trash2 },
  { id: "kitchen", name: "Küchenmontage", icon: ChefHat },
];

const AVAILABILITY_OPTIONS = [
  { id: "immediate", label: "Sofort verfügbar" },
  { id: "week", label: "Diese Woche" },
  { id: "twoweeks", label: "In 1-2 Wochen" },
  { id: "month", label: "In einem Monat" },
];

const ProviderRegisterPage = () => {
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [businessType, setBusinessType] = useState(""); // "business" or "individual"
  const [businessName, setBusinessName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [postalCode, setPostalCode] = useState("");
  const [serviceRadius, setServiceRadius] = useState("25");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [availability, setAvailability] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const totalSteps = 7;
  
  const toggleService = (serviceId) => {
    setSelectedServices((prev) => 
      prev.includes(serviceId) 
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");

    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const payload = {
        business_type: businessType,
        business_name: businessName,
        owner_name: ownerName,
        email: email.trim().toLowerCase(),
        phone,
        services: selectedServices,
        postal_code: postalCode,
        service_radius: parseInt(serviceRadius || "25", 10),
        availability,
        description,
      };

      const res = await axios.post(apiUrl("/provider/register"), payload, { headers });
      if (res.data?.ok) {
        setSubmitted(true);
      } else {
        setError(res.data?.detail || "Profil konnte nicht eingereicht werden.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Fehler beim Senden. Bitte versuchen Sie es erneut.";
      setError(typeof msg === "string" ? msg : "Fehler beim Senden.");
    } finally {
      setSubmitting(false);
    }
  };
  
  const canProceed = () => {
    switch (step) {
      case 1: return businessType !== "";
      case 2: return businessType === "individual" ? ownerName.length >= 2 : businessName.length >= 2;
      case 3: return selectedServices.length > 0;
      case 4: return postalCode.length >= 4;
      case 5: return phone && email && email.includes("@");
      case 6: return availability !== "";
      default: return true;
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 pb-20 md:pb-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Profil eingereicht!</h2>
            <p className="text-gray-600 mb-6">
              Danke. Ihr Anbieterprofil wurde eingereicht und wird geprüft. Wir melden uns in Kürze bei Ihnen.
            </p>
            <div className="space-y-3">
              <Button 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => navigate("/")}
              >
                Zurück zur Startseite
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate("/login")}
              >
                Zum Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-4">
      {/* Progress Bar */}
      <div className="sticky top-0 z-40 bg-white border-b shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <button 
              onClick={() => step > 1 ? setStep(step - 1) : navigate("/")}
              className="p-2 -ml-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium text-gray-600">
              Schritt {step} von {totalSteps}
            </span>
            <div className="w-10" />
          </div>
          <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-600 transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Business Type */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Wie möchten Sie sich registrieren?</h2>
            <p className="text-gray-600 mb-6">Wählen Sie Ihren Geschäftstyp</p>
            
            <div className="space-y-4">
              <button
                onClick={() => setBusinessType("business")}
                className={`w-full p-6 rounded-xl border-2 transition-all text-left flex items-start gap-4 ${
                  businessType === "business" 
                    ? "border-green-600 bg-green-50" 
                    : "border-gray-200 bg-white hover:border-green-200"
                }`}
              >
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Gewerblich / Firma</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Ich registriere mich als Unternehmen oder Handwerksbetrieb
                  </p>
                </div>
              </button>
              
              <button
                onClick={() => setBusinessType("individual")}
                className={`w-full p-6 rounded-xl border-2 transition-all text-left flex items-start gap-4 ${
                  businessType === "individual" 
                    ? "border-green-600 bg-green-50" 
                    : "border-gray-200 bg-white hover:border-green-200"
                }`}
              >
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <User className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Selbstständig / Freelancer</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Ich arbeite als selbstständiger Handwerker
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Business/Personal Name */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {businessType === "business" ? "Firmeninformationen" : "Persönliche Informationen"}
            </h2>
            <p className="text-gray-600 mb-6">
              {businessType === "business" 
                ? "Geben Sie Ihre Firmendaten ein" 
                : "Geben Sie Ihren Namen ein"}
            </p>
            
            <div className="space-y-4">
              {businessType === "business" && (
                <div>
                  <Label>Firmenname *</Label>
                  <Input
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Mustermann Handwerk GmbH"
                    className="mt-1 h-12"
                  />
                </div>
              )}
              
              <div>
                <Label>{businessType === "business" ? "Inhaber/Ansprechpartner" : "Vollständiger Name *"}</Label>
                <Input
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="Max Mustermann"
                  className="mt-1 h-12"
                />
              </div>
              
              <div>
                <Label>Kurze Beschreibung (optional)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Über 20 Jahre Erfahrung im Bereich..."
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Service Categories */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Welche Services bieten Sie an?</h2>
            <p className="text-gray-600 mb-6">Wählen Sie alle zutreffenden Kategorien</p>
            
            <div className="grid grid-cols-2 gap-3">
              {SERVICE_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const isSelected = selectedServices.includes(cat.id);
                
                return (
                  <button
                    key={cat.id}
                    onClick={() => toggleService(cat.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected 
                        ? "border-green-600 bg-green-50" 
                        : "border-gray-200 bg-white hover:border-green-200"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isSelected ? "bg-green-600 text-white" : "bg-gray-100 text-gray-600"
                      }`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className={`font-medium text-sm ${isSelected ? "text-green-700" : "text-gray-900"}`}>
                        {cat.name}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            
            <p className="text-sm text-gray-500 mt-4">
              {selectedServices.length} Kategorie(n) ausgewählt
            </p>
          </div>
        )}

        {/* Step 4: Service Area */}
        {step === 4 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">In welchem Gebiet arbeiten Sie?</h2>
            <p className="text-gray-600 mb-6">Definieren Sie Ihren Arbeitsbereich</p>
            
            <div className="space-y-4">
              <div>
                <Label>Postleitzahl (Standort) *</Label>
                <Input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="20095"
                  className="mt-1 h-12"
                />
              </div>
              
              <div>
                <Label>Umkreis in km</Label>
                <select
                  value={serviceRadius}
                  onChange={(e) => setServiceRadius(e.target.value)}
                  className="mt-1 w-full h-12 px-3 border border-gray-300 rounded-md"
                >
                  <option value="10">10 km</option>
                  <option value="25">25 km</option>
                  <option value="50">50 km</option>
                  <option value="75">75 km</option>
                  <option value="100">100 km</option>
                </select>
              </div>
              
              <div className="flex items-center p-3 bg-green-50 rounded-lg">
                <MapPin className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm text-green-700">
                  Pilot-Region: Hamburg & Schleswig-Holstein
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Contact */}
        {step === 5 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Kontaktdaten</h2>
            <p className="text-gray-600 mb-6">So können Kunden Sie erreichen</p>
            
            <div className="space-y-4">
              <div>
                <Label>Telefon *</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+49 40 123456"
                  className="mt-1 h-12"
                />
              </div>
              
              <div>
                <Label>E-Mail *</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="kontakt@firma.de"
                  className="mt-1 h-12"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Availability */}
        {step === 6 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Ihre Verfügbarkeit</h2>
            <p className="text-gray-600 mb-6">Wann können Sie neue Aufträge annehmen?</p>
            
            <div className="space-y-3">
              {AVAILABILITY_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setAvailability(opt.id)}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                    availability === opt.id 
                      ? "border-green-600 bg-green-50" 
                      : "border-gray-200 bg-white hover:border-green-200"
                  }`}
                >
                  <Clock className={`h-5 w-5 ${availability === opt.id ? "text-green-600" : "text-gray-400"}`} />
                  <span className={`font-medium ${availability === opt.id ? "text-green-700" : "text-gray-900"}`}>
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 7: Review & Submit */}
        {step === 7 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Profil überprüfen</h2>
            <p className="text-gray-600 mb-6">Überprüfen Sie Ihre Angaben vor dem Absenden</p>
            
            <Card>
              <CardContent className="p-4 space-y-3">
                <div>
                  <span className="text-sm text-gray-500">Typ</span>
                  <p className="font-medium">{businessType === "business" ? "Gewerblich" : "Selbstständig"}</p>
                </div>
                {businessType === "business" && (
                  <div>
                    <span className="text-sm text-gray-500">Firma</span>
                    <p className="font-medium">{businessName}</p>
                  </div>
                )}
                <div>
                  <span className="text-sm text-gray-500">Name</span>
                  <p className="font-medium">{ownerName}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Services</span>
                  <p className="font-medium">{selectedServices.length} Kategorie(n)</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Arbeitsgebiet</span>
                  <p className="font-medium">{postalCode}, Umkreis {serviceRadius} km</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Kontakt</span>
                  <p className="font-medium">{phone}</p>
                  <p className="text-sm text-gray-600">{email}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Verfügbarkeit</span>
                  <p className="font-medium">
                    {AVAILABILITY_OPTIONS.find(o => o.id === availability)?.label}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Nach der Registrierung prüfen wir Ihr Profil. Sie erhalten eine Bestätigung per E-Mail.
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-8 flex gap-3">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-12"
            >
              Zurück
            </Button>
          )}
          
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="flex-1 h-12 bg-green-600 hover:bg-green-700"
            >
              Weiter <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 h-12 bg-green-600 hover:bg-green-700"
            >
              {submitting ? "Wird gesendet..." : "Profil einreichen"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderRegisterPage;
